import {
  ADJACENT_OFFSETS,
  cellIndex,
  coordinate,
  coordinateAt,
  isOnBoard,
  progressAt,
  type GameState,
  type Player,
} from "../game";
import { generateLegalMovesUnchecked } from "../game/rules";

export const TERMINAL_SCORE = 1_000_000;
export const HEURISTIC_SCORE_LIMIT = TERMINAL_SCORE - 1;

export type TerminalAssessment = "win" | "loss" | "draw" | null;

export type PositionEvaluation = Readonly<{
  score: number;
  terminal: TerminalAssessment;
}>;

const WEIGHTS = Object.freeze({
  progress: 26,
  mobility: 8,
  movableDisc: 34,
  buriedDisc: 10,
  controlledStack: 12,
  supportedField: 3,
  vulnerableField: 18,
});

function signFor(player: Player, perspective: Player): 1 | -1 {
  return player === perspective ? 1 : -1;
}

function existingNeighborCount(state: GameState, index: number): number {
  const position = coordinateAt(index);
  let count = 0;

  for (const offset of ADJACENT_OFFSETS) {
    const neighbor = coordinate(position.row + offset.row, position.column + offset.column);
    if (isOnBoard(neighbor) && state.cells[cellIndex(neighbor)]?.kind === "field") count += 1;
  }
  return count;
}

/**
 * Scores a canonical state from one fixed player's perspective. Terminal results
 * are deliberately outside the heuristic range so search can never prefer style
 * points over a proven win or loss.
 */
export function evaluateState(state: GameState, perspective: Player): PositionEvaluation {
  if (state.outcome !== null) {
    const won = state.outcome.winner === perspective;
    return Object.freeze({
      score: won ? TERMINAL_SCORE : -TERMINAL_SCORE,
      terminal: won ? "win" : "loss",
    });
  }

  let score = 0;
  for (let index = 0; index < state.cells.length; index += 1) {
    const cell = state.cells[index];
    if (cell?.kind !== "field" || cell.stack.length === 0) continue;

    const position = coordinateAt(index);
    const topIndex = cell.stack.length - 1;
    const neighbors = existingNeighborCount(state, index);

    for (let stackIndex = 0; stackIndex < cell.stack.length; stackIndex += 1) {
      const disc = cell.stack[stackIndex];
      if (disc === undefined) continue;
      const sign = signFor(disc, perspective);
      score += sign * progressAt(disc, position) * WEIGHTS.progress;
      score += sign * (stackIndex === topIndex ? WEIGHTS.movableDisc : WEIGHTS.buriedDisc);
    }

    const controller = cell.stack[topIndex];
    if (controller !== undefined) {
      const sign = signFor(controller, perspective);
      score += sign * cell.stack.length * WEIGHTS.controlledStack;
      score += sign * Math.min(neighbors, 4) * WEIGHTS.supportedField;
      if (neighbors <= 2) score -= sign * WEIGHTS.vulnerableField;
    }
  }

  const activeSign = signFor(state.activePlayer, perspective);
  const legalMoves = generateLegalMovesUnchecked(state);
  score += activeSign * legalMoves.length * WEIGHTS.mobility;
  score +=
    activeSign *
    legalMoves.filter((move) => move.kind === "passage").length *
    (TERMINAL_SCORE / 10);

  return Object.freeze({
    score: Math.max(-HEURISTIC_SCORE_LIMIT, Math.min(HEURISTIC_SCORE_LIMIT, score)),
    terminal: null,
  });
}
